import Cookies from 'universal-cookie'

/**
 * Utility for storing and retrieving userInfo.
 * Migrated from cookies to localStorage for better security and performance.
 * Includes backward compatibility to migrate existing cookie data.
 */
export const UserInfoStorage = {
  /**
   * Get userInfo from localStorage, with backward compatibility for cookie migration
   */
  get(): any {
    try {
      const stored = localStorage.getItem('userInfo')
      if (stored && stored.trim() !== '') {
        const parsed = JSON.parse(stored)
        return parsed
      }
      
      // Backward compatibility: check cookie and migrate
      return UserInfoStorage.migrateFromCookie()
    } catch (e) {
      // If parsing fails, localStorage might be corrupted - clear it and try migration
      console.error('Failed to get userInfo from localStorage:', e)
      try {
        localStorage.removeItem('userInfo')
      } catch (clearError) {
        // Ignore clear errors
      }
      return UserInfoStorage.migrateFromCookie()
    }
  },

  /**
   * Set userInfo in localStorage and clear old cookie
   */
  set(userInfo: any): void {
    if (!userInfo || typeof userInfo !== 'object') {
      console.warn('UserInfoStorage.set: Invalid userInfo data', userInfo)
      return
    }

    try {
      const serialized = JSON.stringify(userInfo)
      localStorage.setItem('userInfo', serialized)
      // Clear old cookie during migration period
      UserInfoStorage.clearCookie()
    } catch (e) {
      // Handle quota exceeded errors specifically
      if (e instanceof DOMException && e.name === 'QuotaExceededError') {
        console.error('localStorage quota exceeded. Attempting to clear old data...')
        // Try to clear and retry once
        try {
          localStorage.removeItem('userInfo')
          localStorage.setItem('userInfo', JSON.stringify(userInfo))
          UserInfoStorage.clearCookie()
        } catch (retryError) {
          console.error('Failed to set userInfo after quota cleanup:', retryError)
        }
      } else {
        console.error('Failed to set userInfo:', e)
      }
    }
  },

  /**
   * Remove userInfo from localStorage and cookies
   */
  remove(): void {
    try {
      localStorage.removeItem('userInfo')
      UserInfoStorage.clearCookie()
    } catch (e) {
      console.error('Failed to remove userInfo:', e)
    }
  },

  /**
   * Migrate userInfo from cookie to localStorage (backward compatibility)
   * @internal
   */
  migrateFromCookie(): any {
    try {
      const cookies = new Cookies()
      const cookieData = cookies.get('userInfo')
      
      if (cookieData) {
        // Handle both string and object formats (universal-cookie auto-parses JSON)
        let dataToStore = cookieData
        if (typeof cookieData === 'string') {
          try {
            dataToStore = JSON.parse(cookieData)
          } catch (parseError) {
            // If it's a string that's not JSON, it might be old format - skip migration
            console.warn('Cookie data is string but not valid JSON, skipping migration')
            return null
          }
        }

        // Store in localStorage (this will also clear the cookie)
        try {
          localStorage.setItem('userInfo', JSON.stringify(dataToStore))
          // Remove cookie after successful migration
          cookies.remove('userInfo', { path: '/' })
        } catch (storageError) {
          // If localStorage fails, still return the cookie data
          console.error('Failed to migrate cookie to localStorage:', storageError)
          return dataToStore
        }

        return dataToStore
      }
    } catch (e) {
      // Ignore cookie errors during migration (cookie might not exist)
      console.debug('Cookie migration check failed (expected if no cookie exists):', e)
    }
    return null
  },

  /**
   * Clear userInfo cookie (for migration cleanup)
   * @internal
   */
  clearCookie(): void {
    try {
      const cookies = new Cookies()
      cookies.remove('userInfo', { path: '/' })
    } catch (e) {
      // Ignore cookie errors (cookie might not exist)
    }
  }
}

